/**
 * FeatureFlagsContext - Provider global pour les feature flags par organisation
 * 
 * Expose une fonction isEnabled(key) qui vérifie :
 * 1. Les flags spécifiques à l'organisation (DB)
 * 2. Les flags globaux (FEATURE_FLAGS config)
 * 3. Clé déclarée sans réglage : activée par défaut
 * 4. Clé inconnue : FERMÉE (et signalée en console)
 */

import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useOrganizationFeatureFlags, resolveFeatureFlag, FeatureFlagKey } from '@/hooks/useOrganizationFeatureFlags';

interface FeatureFlagsContextValue {
  /** Vérifie si une feature est activée (org-specific > global > default true) */
  isEnabled: (key: FeatureFlagKey | string) => boolean;
  /** True pendant le chargement des flags */
  isLoading: boolean;
  /** Map des flags chargés depuis la DB */
  flags: Record<string, boolean>;
  /** Recharger les flags */
  refetch: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { isEnabled, isLoading, flags, refetch } = useOrganizationFeatureFlags();
  
  // Debug logs en dev
  useEffect(() => {
    if (!isLoading && Object.keys(flags).length > 0) {
      console.log('[FeatureFlags] Loaded org flags:', flags);
    }
  }, [isLoading, flags]);
  
  return (
    <FeatureFlagsContext.Provider value={{ isEnabled, isLoading, flags, refetch }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook pour accéder aux feature flags
 * Hors provider (pages non authentifiées) : seuls les défauts globaux
 * s'appliquent, et une clé inconnue reste fermée.
 */
export function useFeatureFlags(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext);
  
  if (!context) {
    // Fallback pour les pages non authentifiées ou hors provider
    // Par défaut : vérifier seulement les flags globaux
    return {
      // Même résolution que dans le provider, sans les lignes de la base :
      // défaut global, puis `true` pour une clé déclarée, `false` pour une clé
      // inconnue. Hors provider il n'y a pas de session, donc pas
      // d'organisation : une clé per-org comme 'partner_offers' porte son
      // défaut fermé (FEATURE_FLAGS.PARTNER_OFFERS_ENABLED = false).
      isEnabled: (key: FeatureFlagKey | string): boolean => resolveFeatureFlag(key, {}),
      isLoading: false,
      flags: {},
      refetch: async () => {}
    };
  }
  
  return context;
}

/**
 * Composant utilitaire pour le rendu conditionnel basé sur un flag
 */
interface FeatureGateProps {
  feature: FeatureFlagKey | string;
  children: ReactNode;
  fallback?: ReactNode;
}

export function FeatureGate({ feature, children, fallback = null }: FeatureGateProps) {
  const { isEnabled, isLoading } = useFeatureFlags();
  
  // Pendant le chargement, on affiche le fallback pour éviter les flash
  if (isLoading) {
    return <>{fallback}</>;
  }
  
  return isEnabled(feature) ? <>{children}</> : <>{fallback}</>;
}
